import React, { useEffect } from "react";
import { Alert, Form, Input, Radio, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { ChatType } from "@fitness-app/data-models/entities/Chat";
import { DEFAULT_ANNOUNCEMENT_CHANNEL_NAME } from "@fitness-app/data-models/entities/TrainerConfig";

import { type ChatConfigurationFormModel } from "./types";

interface ChatConfigurationFormProps {
  formController?: FormInstance<ChatConfigurationFormModel>;
  onSubmit: (formData: ChatConfigurationFormModel) => void;
  model?: Partial<ChatConfigurationFormModel> | null;
}

// The client has ONE announcement channel; account-level defaults live in the chat drawer
// panel (menu → "Kanał ogłoszeniowy"). This form only overrides them for clients activated
// through this automation — including a channel that already exists (TRE-801).
const ChatConfigurationForm = ({ model, onSubmit, formController }: ChatConfigurationFormProps) => {
  const { t } = useTranslation(["automation", "common"]);
  const mode = Form.useWatch("announcementChannelMode", formController);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model]);

  return (
    <Form<ChatConfigurationFormModel>
      name="form"
      layout="vertical"
      form={formController}
      initialValues={{ announcementChannelMode: "inherit" }}
      onFinish={onSubmit}
    >
      <Alert
        type="info"
        showIcon
        className="mb-6"
        message="Klientka ma jeden kanał ogłoszeniowy. Domyślnymi ustawieniami (włączenie, nazwa, typ) zarządzasz w oknie czatu (menu → Kanał ogłoszeniowy) — poniżej możesz je nadpisać dla klientek z tej automatyzacji."
      />

      <Form.Item name="announcementChannelMode" label="Kanał ogłoszeniowy dla klientek z tej automatyzacji">
        <Radio.Group>
          <Radio value="inherit">Użyj ustawień konta</Radio>
          <Radio value="enabled">Włącz z własnymi ustawieniami</Radio>
          <Radio value="disabled">Nie twórz dla tej automatyzacji</Radio>
        </Radio.Group>
      </Form.Item>

      {mode === "enabled" && (
        <>
          <Form.Item
            name="announcementChannelName"
            label="Nazwa kanału"
            tooltip="Puste pole = nazwa z ustawień konta. Przy aktywacji klientki nadpisze też nazwę już istniejącego kanału."
          >
            <Input placeholder={DEFAULT_ANNOUNCEMENT_CHANNEL_NAME} maxLength={80} />
          </Form.Item>

          <Form.Item name="announcementChannelType" label="Typ kanału">
            <Radio.Group>
              <Radio value={null}>Jak w ustawieniach konta</Radio>
              <Radio value={ChatType.OneWayMarketing}>Jednokierunkowy</Radio>
              <Radio value={ChatType.TwoWayMarketing}>Dwukierunkowy</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      <Form.Item
        valuePropName="checked"
        name="sendByDefaultMessageFromAutomationToMarketingChannel"
        label={t<string>("chatConfig.sendByDefaultMessageFromAutomationToMarketingChannel")}
      >
        <Switch disabled={mode === "disabled"} />
      </Form.Item>
    </Form>
  );
};

export default ChatConfigurationForm;
